import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import type { GridBreakVariant, GridGap, GridVariant } from './grid.model';
import styles from './grid.styles';

/**
 * @slot default - The grid items
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default wrapper.
 */
export class BlocksGrid extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Style variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**side-by-side** yields a grid whose grid items display side-by-side (2 per row) on all screen sizes</li>
   * <li>**2up** yields a grid whose grid items are stacked on small screens but display side-by-side when enough screen real estate is available to do so</li>
   * <li>**2-3up** yields a grid whose grid items display side-by-side (2 per row) on small screens and 3 per row on md/lg screens</li>
   * <li>**3up** yields a grid whose grid items are stacked on small screens, transforms to a 2-across pattern and then transforms again to a 3-across pattern</li>
   * <li>**1-3up** yields a grid whose grid items are stacked on small screens and transforms to a 3-across pattern on larger screens</li>
   * <li>**4up** yields a grid whose grid items are stacked on small screens, transforms to a 2-across pattern, transforms again to a 3-across pattern, and ultimately transforms to a 4-across pattern</li>
   * <li>**2-3-4up** yields a grid whose grid items are 2-across on small screens, 3-across on medium screens, and ultimately transforms to a 4-across pattern on  lg screens</li>
   * <li>**2-4up** yields a grid whose grid items are 2-across on small screens and ultimately transforms to a 4-across pattern on md/lg screens</li>
   * <li>**1-2-4up** yields a grid whose grid items are stacked on small screens, transforms to a 2-across pattern, and ultimately transforms to a 4-across pattern</li>
   * <li>**1-4up** yields a grid whose grid items are stacked on small screens, transforms to a 4-across pattern on medium/large screens</li>
   * <li>**2-4-5up** yields a grid whose grid items are 2-across on small screens, 4-across pattern on md screens, and 5up on large screens</li>
   * <li>**5up** yields a grid whose grid items are stacked on small screens, 2up on sm/md screens, 3up on md screens, 4up on md/lg screens, then 5up on lg screens
   * <li>**2-3-4-5up** yields a grid whose grid items are  2up on sm/md screens, 3up on md screens, 4up on md/lg screens, then 5up on lg screens
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: GridVariant;

  /**
   * Style variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**none** yields a grid whose grid items are spaced without any gutter in between</li>
   * <li>**xs** yields a grid whose grid items are spaced with a gap smaller than the small</li>
   * <li>**sm** yields a grid whose grid items are spaced with a gap smaller than the default</li>
   * <li>**lg** yields a grid whose grid items are spaced with a gap larger than the default</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  gap: GridGap;

  /**
   * Break variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **faster** breaks the grid at a smaller width than the default. Example: 2up grid breaks to 2 per row at smaller width than default</li>
   * <li> **slower** breaks the grid at a larger width than the default. Example: 2up grid breaks to 2 per row at larger width than default</li>
   * <li> **none** the grid does not break.</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  break: GridBreakVariant;

  /**
   * Separators
   * 1) Adds lines between items
   */
  @property({ type: Boolean })
  separators: boolean | undefined;

  /**
   * Height variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **auto** Prevents the grid from stretching so the grid items are staggered in height</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  height: 'auto' | undefined;

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-grid', {
      'cat-c-grid--side-by-side': this.variant === 'side-by-side',
      'cat-c-grid--2up': this.variant === '2up',
      'cat-c-grid--2-3up': this.variant === '2-3up',
      'cat-c-grid--3up': this.variant === '3up',
      'cat-c-grid--1-3up': this.variant === '1-3up',
      'cat-c-grid--4up': this.variant === '4up',
      'cat-c-grid--1-4up': this.variant === '1-4up',
      'cat-c-grid--2-3-4up': this.variant === '2-3-4up',
      'cat-c-grid--2-4up': this.variant === '2-4up',
      'cat-c-grid--1-2-4up': this.variant === '1-2-4up',
      'cat-c-grid--2-4-5up': this.variant === '2-4-5up',
      'cat-c-grid--2-3-4-5up': this.variant === '2-3-4-5up',
      'cat-c-grid--5up': this.variant === '5up',
      'cat-c-grid--2-4-6up': this.variant === '2-4-6up',
      'cat-c-grid--gap-none': this.gap === 'none',
      'cat-c-grid--gap-xs': this.gap === 'xs',
      'cat-c-grid--gap-sm': this.gap === 'sm',
      'cat-c-grid--gap-lg': this.gap === 'lg',
      'cat-c-grid--break-faster': this.break === 'faster',
      'cat-c-grid--break-slower': this.break === 'slower',
      'cat-c-grid--break-none': this.break === 'none',
      'cat-c-grid--separators': this.separators === true,
      'cat-c-grid--height-auto': this.height === 'auto'
    });

    return html`
      <div part="base" class="${componentClassName}">
        <slot part="part-default"></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-grid') === undefined) {
  customElements.define('cat-grid', BlocksGrid);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-grid': BlocksGrid;
  }
}
